import * as React from "react";
import { Box, Button, Checkbox, Typography } from "@mui/material";
import { connect, useDispatch, useSelector } from "react-redux";
import produce from "immer";
import HOPI from "./HOPI";
import PastHistoryOfAllergy from "./PastHistoryOfAllergy";
import PresentHealthStatus from "./PresentHealthStatus";
import { patchMedicalTest } from "../../../actions/medicalTest";
import { LabStatuses } from "../../../interfaces/Lab";
import { RootState } from "../../../store";
import { notificationAdd } from "../../../actions/notification";
import { tl } from "../../../components/translate";
import Can from "../../Policy/Can";
import MedicalAssessmentRemarks from "./MedicalAssessmentRemarks";

function CreateAssessment({ id, updateTest, medicalTest }) {
  const [assessment, setAssessment] = React.useState({
    hopi: "None",
    pastHistoryOfAllergy: "None",
    presentHealthStatus: "Fit",
    remarks: ""
  });
  const dispatch = useDispatch();
  React.useEffect(() => {
    const relatedAssessment = medicalTest?.results?.assessment;
    const defaultAssessment = {
      hopi: "None",
      pastHistoryOfAllergy: "None",
      presentHealthStatus: "Fit",
      remarks: ""
    };
    if (relatedAssessment) {
      if (relatedAssessment.hopi) {
        defaultAssessment.hopi = relatedAssessment.hopi;
      }
      if (relatedAssessment.pastHistoryOfAllergy) {
        defaultAssessment.pastHistoryOfAllergy = relatedAssessment.pastHistoryOfAllergy;
      }
      if (relatedAssessment.presentHealthStatus) {
        defaultAssessment.presentHealthStatus = relatedAssessment.presentHealthStatus;
      }
      if (relatedAssessment.remarks) {
        defaultAssessment.remarks = relatedAssessment.remarks;
      }
    }
    setAssessment(defaultAssessment);
  }, [medicalTest]);

  const rc = useSelector(
    (state: RootState) =>
      state.resources.resourceCentres.find((r) => r.id === state.userContext.resourceCentreId) ||
      state.userContext.resourceCentre
  );

  const authorizedSP = rc?.settings?.medicalSettings;
  const [saving, setSaving] = React.useState(false);
  const [sendToClient, setSendToClient] = React.useState(true);

  return (
    <Box width="110%" marginLeft="-16px" marginTop="16px">
      <Can policyAccessKey="medical:manageAssessment">
        <>
          <Box>
            <HOPI
              assessmentHOPI={assessment.hopi}
              onChange={(value) => {
                setAssessment({ ...assessment, hopi: value });
              }}
            />
          </Box>
          <Box mt="8px">
            <PastHistoryOfAllergy
              assessmentPastHistoryOfAllergy={assessment.pastHistoryOfAllergy}
              onChange={(value) => {
                setAssessment({ ...assessment, pastHistoryOfAllergy: value });
              }}
            />
          </Box>
          <Box mt="8px">
            <PresentHealthStatus
              assessmentPresentHealthStatus={assessment.presentHealthStatus}
              onChange={(value) => {
                setAssessment({ ...assessment, presentHealthStatus: value });
              }}
            />
          </Box>
          <Box my={4}>
            <MedicalAssessmentRemarks
              onChange={(value) => {
                setAssessment({ ...assessment, remarks: value });
              }}
              value={assessment.remarks}
            />
          </Box>
        </>
      </Can>
      <Box
        sx={{
          padding: "20px 15px 0 5px",
          display: "flex",
          justifyContent: "space-between"
        }}
      >
        <Box display="flex" alignItems="center">
          <Checkbox checked={sendToClient} onChange={() => setSendToClient(!sendToClient)} />
          <Typography>{tl("medical.sendMessageToClient")}</Typography>
        </Box>
        <Box display="flex" alignItems="center" justifyContent="flex-end" gap={4}>
          {medicalTest.status !== LabStatuses.RESULT_READY && (
            <Button
              data-testmation="saveAsDraftMedical"
              variant="outlined"
              color="primary"
              disabled={saving}
              onClick={async () => {
                setSaving(true);
                try {
                  const copy: any = produce(medicalTest, (draft) => {
                    draft.results.assessment = assessment;
                  });
                  const toPost = {
                    attachments: [],
                    authorizedSP1Id: authorizedSP.authorizedSP1,
                    authorizedSP2Id: authorizedSP.authorizedSP2,
                    authorizedSP3Id: authorizedSP.authorizedSP3,
                    authorizedSP4Id: authorizedSP.authorizedSP4,
                    final: false,
                    updatedTestResults: copy.results,
                    sendToClient
                  };
                  await updateTest(toPost, id);
                } catch (err) {
                  dispatch(
                    notificationAdd({
                      id: new Date().getUTCMilliseconds(),
                      variant: "error",
                      message: err?.data?.message || "Failed to update medical test",
                      autoTimeout: false
                    })
                  );
                }
                setSaving(false);
              }}
            >
              Save as Draft
            </Button>
          )}
          <Can policyAccessKey="medical:finaliseMedical">
            <Button
              data-testmation="creaseAssessment"
              variant="contained"
              color="primary"
              disabled={saving}
              onClick={async () => {
                setSaving(true);
                try {
                  const copy: any = produce(medicalTest, (draft) => {
                    draft.results.assessment = assessment;
                  });
                  const toPost = {
                    attachments: [],
                    authorizedSP1Id: authorizedSP.authorizedSP1,
                    authorizedSP2Id: authorizedSP.authorizedSP2,
                    authorizedSP3Id: authorizedSP.authorizedSP3,
                    authorizedSP4Id: authorizedSP.authorizedSP4,
                    final: true,
                    updatedTestResults: copy.results,
                    sendToClient
                  };
                  await updateTest(toPost, id);
                } catch (err) {
                  dispatch(
                    notificationAdd({
                      id: new Date().getUTCMilliseconds(),
                      variant: "error",
                      message: err?.data?.message || "Failed to update medical test",
                      autoTimeout: false
                    })
                  );
                }
                setSaving(false);
              }}
            >
              Finalize
            </Button>
          </Can>
        </Box>
      </Box>
    </Box>
  );
}

function mapStateToProps(state, ownProps) {
  const medicalTest = state.medicalTest.collection.find((item) => item.id === Number(ownProps.id));

  return {
    medicalTest
  };
}
const mapDispatchToProps = (dispatch) => ({
  updateTest: (data, id) => dispatch(patchMedicalTest(data, id))
});

export default connect(mapStateToProps, mapDispatchToProps)(CreateAssessment);
